import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search,
  X,
  ArrowRight,
  Newspaper,
  Briefcase,
  Award,
  FileText,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  page: { label: "Page", icon: FileText, color: "text-sky-600 bg-sky-50" },
  blog: { label: "Blog", icon: Newspaper, color: "text-amber-600 bg-amber-50" },
  "case-study": { label: "Case Study", icon: Award, color: "text-emerald-600 bg-emerald-50" },
  job: { label: "Career", icon: Briefcase, color: "text-violet-600 bg-violet-50" },
};

/** Highlight occurrences of `query` inside `text` (case-insensitive). */
function highlight(text, query) {
  if (!query || !text) return text;
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${safe})`, "ig"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="bg-yellow-100 text-slate-900 rounded px-0.5">{part}</mark>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

export default function SearchModal({ open, onClose }) {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  // Focus the input + reset state when opening; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setResults([]);
    setActiveIdx(0);
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    document.body.style.overflow = "hidden";
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
  }, [open]);

  // Escape closes the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search`, { params: { q, limit: 15 } });
        setResults(Array.isArray(data?.results) ? data.results : []);
        setActiveIdx(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, open]);

  // Arrow-key navigation + Enter to jump.
  const handleKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = results[activeIdx];
      if (target) {
        navigate(target.path);
        onClose();
      }
    }
  };

  if (!open) return null;

  // Group results by type for visual sectioning.
  const grouped = results.reduce((acc, r) => {
    (acc[r.type] ||= []).push(r);
    return acc;
  }, {});
  const TYPE_ORDER = ["page", "blog", "case-study", "job"];

  // Build a global index → result list mapping so arrow keys can follow groups.
  let globalIdx = -1;

  return (
    <div
      data-testid="search-modal"
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[10vh] sm:pt-[14vh]"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" aria-hidden />

      {/* Panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="h-5 w-5 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, blog posts, case studies, jobs…"
            data-testid="search-input"
            className="flex-1 bg-transparent text-base outline-none placeholder:text-slate-400"
            autoComplete="off"
          />
          {loading && <Loader2 className="h-4 w-4 text-sky-500 animate-spin" />}
          <button
            type="button"
            onClick={onClose}
            data-testid="search-close"
            aria-label="Close search"
            className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto" data-testid="search-results-list">
          {query.trim().length < 2 && (
            <div className="px-6 py-10 text-center text-sm text-slate-500">
              <p>Type at least 2 characters to search.</p>
              <p className="mt-2 text-xs text-slate-400">
                Try <span className="font-mono text-sky-600">PunchOut catalog</span>,{" "}
                <span className="font-mono text-sky-600">QuickBooks</span>, or{" "}
                <span className="font-mono text-sky-600">sign companies</span>.
              </p>
            </div>
          )}

          {query.trim().length >= 2 && !loading && results.length === 0 && (
            <div data-testid="search-empty" className="px-6 py-10 text-center text-sm text-slate-500">
              No results for "<span className="font-semibold text-slate-700">{query}</span>".
              <p className="mt-1 text-xs text-slate-400">Try fewer or different keywords.</p>
            </div>
          )}

          {results.length > 0 && (
            <div className="py-2">
              {TYPE_ORDER.filter((t) => grouped[t]?.length).map((typeKey) => {
                const meta = TYPE_META[typeKey] || TYPE_META.page;
                const TypeIcon = meta.icon;
                return (
                  <div key={typeKey} className="mb-2 last:mb-0">
                    <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      {meta.label}s
                    </div>
                    <ul>
                      {grouped[typeKey].map((r) => {
                        globalIdx += 1;
                        const isActive = globalIdx === activeIdx;
                        const myIdx = globalIdx;
                        return (
                          <li key={`${typeKey}-${r.path}`}>
                            <Link
                              to={r.path}
                              onClick={onClose}
                              onMouseEnter={() => setActiveIdx(myIdx)}
                              data-testid={`search-result-${myIdx}`}
                              className={`flex items-start gap-3 px-4 py-3 transition-colors ${
                                isActive ? "bg-sky-50" : "hover:bg-slate-50"
                              }`}
                            >
                              <span
                                className={`mt-0.5 h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${meta.color}`}
                              >
                                <TypeIcon className="h-3.5 w-3.5" />
                              </span>
                              <span className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-900 truncate">
                                  {highlight(r.title, query)}
                                </p>
                                {r.description && (
                                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                    {highlight(r.description, query)}
                                  </p>
                                )}
                              </span>
                              <ArrowRight className="h-3.5 w-3.5 text-slate-300 mt-1.5 shrink-0" />
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400 flex items-center justify-between bg-slate-50/60">
          <span>
            <kbd className="font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5">↑↓</kbd> navigate
            <span className="mx-2">·</span>
            <kbd className="font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5">enter</kbd> open
          </span>
          <span>
            <kbd className="font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5">esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}
